function tryDecode(value: string): string | null {
    try {
        return decodeURIComponent(value);
    } catch {
        return null;
    }
}

function safeDecode(value: string): string {
    let decoded = tryDecode(value);
    return decoded === null ? value : decoded;
}

/**
 * Extracts the download filename from a `Content-Disposition` header.
 *
 * Prefers the RFC 5987 `filename*=UTF-8''...` form (always percent-encoded, never
 * quoted). Falls back to the plain `filename=` form, stripping the surrounding
 * double-quotes that browsers would otherwise treat as part of the name — on
 * Windows those quotes are illegal and get replaced with `_`, producing names
 * like `_my file.jpg_` with a broken extension.
 */
export function parseContentDispositionFilename(contentDisposition: string | null): string {
    if (!contentDisposition) {
        return 'download';
    }

    // Prefer the RFC 5987 form, but only when its value actually decodes. A
    // non-UTF-8 charset (e.g. ISO-8859-1) or a malformed sequence makes
    // decodeURIComponent throw — in that case fall through to the plain
    // `filename=` rather than handing back a raw, undecoded token.
    let fallback = 'download';
    let extended = contentDisposition.match(/filename\*=(?:[^']*'[^']*')?([^;]+)/i);
    if (extended && extended[1]) {
        let raw = extended[1].trim();
        let decoded = tryDecode(raw);
        if (decoded !== null) {
            return decoded;
        }
        // Undecodable, but still preferable to a generic name if no plain
        // `filename=` is present — it at least preserves the extension.
        fallback = raw;
    }

    let plain = contentDisposition.match(/filename=("?)(.*?)\1(?:;|$)/i);
    if (plain && plain[2]) {
        return safeDecode(plain[2].trim());
    }

    return fallback;
}
